import "./Edit.css";
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchSupportItems } from "../../actions/support";
import MarkdownEditor from "./MarkdownEditor";
import {
  Button,
  ButtonGroup,
  Typography,
  Chip,
  Tooltip,
  IconButton,
} from "@mui/material";
import SplitscreenIcon from "@mui/icons-material/Splitscreen";
import ToggleOnIcon from "@mui/icons-material/ToggleOn";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

const INITIAL_MARKDOWN = `\\\\tag{1.1}Introduction

This is a **bold** statement and *italic* emphasis.

Here is an inline equation: $E = mc^2$, and here is another: $\\\\int_0^1 x\\\\,dx = \\\\frac{1}{2}$.

Display equation:
$$
\\\\int_0^\\\\infty e^{-x^2}\\\\,dx = \\\\frac{\\\\sqrt{\\\\pi}}{2}
$$

You can also use **bold** and *italic* together for [external links](https://example.com).

Use the toolbar above to:
- Insert **bold** or *italic* text by selecting and clicking B / I
- Add \\$inline math\\$ or display math blocks
- Apply [colored text](https://color:#e53935) via the palette
- Insert external links or search internal support items
`;

function Edit() {
  const dispatch = useDispatch();
  const supportItems = useSelector(
    (state) => state.SupportReducer.supportItems,
  );

  const [markdown, setMarkdown] = useState(INITIAL_MARKDOWN);
  const [editorMode, setEditorMode] = useState(1);
  const [editorHeight, setEditorHeight] = useState("480px");

  useEffect(() => {
    dispatch(fetchSupportItems());
  }, [dispatch]);

  const handleClear = () => {
    if (window.confirm("Clear the editor? This cannot be undone.")) {
      setMarkdown("");
    }
  };

  const wordCount = markdown.trim().split(/\s+/).filter(Boolean).length;

  return (
    <div className="edit2-page">
      <div className="edit2-page__header">
        <div className="edit2-page__title">
          <Typography variant="h5" fontWeight={700} color="text.primary">
            Markdown Editor
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Edit2 — component sandbox
          </Typography>
        </div>

        <div className="edit2-page__controls">
          <Chip
            label={
              supportItems.length > 0
                ? `${supportItems.length} support items`
                : "Loading support items…"
            }
            size="small"
            color={supportItems.length > 0 ? "success" : "default"}
            variant="outlined"
          />

          <div className="edit2-page__mode-group">
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mr: 0.5 }}
            >
              Mode:
            </Typography>
            <ButtonGroup size="small" variant="outlined">
              <Tooltip title="Split: edit on left, preview on right">
                <Button
                  variant={editorMode === 1 ? "contained" : "outlined"}
                  onClick={() => setEditorMode(1)}
                  startIcon={<SplitscreenIcon />}
                >
                  Split
                </Button>
              </Tooltip>
              <Tooltip title="Toggle: switch between edit and preview tabs">
                <Button
                  variant={editorMode === 2 ? "contained" : "outlined"}
                  onClick={() => setEditorMode(2)}
                  startIcon={<ToggleOnIcon />}
                >
                  Toggle
                </Button>
              </Tooltip>
            </ButtonGroup>
          </div>

          <Tooltip title="Clear editor">
            <IconButton size="small" onClick={handleClear} color="error">
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </div>
      </div>

      <div className="edit2-page__editor">
        <MarkdownEditor
          value={markdown}
          onChange={setMarkdown}
          mode={editorMode}
          supportItems={supportItems}
          placeholder="Start typing your markdown here…"
          height={editorHeight}
        />
      </div>

      <div className="edit2-page__footer">
        <Typography variant="caption" color="text.secondary">
          {markdown.length.toLocaleString()} chars ·{" "}
          {wordCount.toLocaleString()} words &nbsp;·&nbsp; Mode{" "}
          {editorMode === 1 ? "1 — split view" : "2 — toggle view"}
        </Typography>
        <div className="edit2-page__height-controls">
          <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
            Height:
          </Typography>
          {["300px", "480px", "640px", "800px"].map((h) => (
            <Button
              key={h}
              size="small"
              variant={editorHeight === h ? "contained" : "text"}
              onClick={() => setEditorHeight(h)}
              sx={{ minWidth: 0, px: 1, fontSize: 11 }}
            >
              {h}
            </Button>
          ))}
        </div>
      </div>

      {/* Raw markdown output for inspection */}
      <details className="edit2-page__raw">
        <summary>
          <Typography variant="caption" color="text.secondary">
            Raw markdown output
          </Typography>
        </summary>
        <pre className="edit2-page__raw-pre">{markdown}</pre>
      </details>
    </div>
  );
}

export default Edit;
